/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `weight_scale` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class WeightScaleMesg : Mesg {
    public constructor() : super("WeightScale", Profile.MesgNum.WEIGHT_SCALE)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** weight, in kg. */
    public var weight: Double?
        get() = getFieldValue(WEIGHT_FIELD_NUM) as? Double
        set(value) = setFieldValue(WEIGHT_FIELD_NUM, value)

    /** percent_fat, in %. */
    public var percentFat: Double?
        get() = getFieldValue(PERCENT_FAT_FIELD_NUM) as? Double
        set(value) = setFieldValue(PERCENT_FAT_FIELD_NUM, value)

    /** percent_hydration, in %. */
    public var percentHydration: Double?
        get() = getFieldValue(PERCENT_HYDRATION_FIELD_NUM) as? Double
        set(value) = setFieldValue(PERCENT_HYDRATION_FIELD_NUM, value)

    /** visceral_fat_mass, in kg. */
    public var visceralFatMass: Double?
        get() = getFieldValue(VISCERAL_FAT_MASS_FIELD_NUM) as? Double
        set(value) = setFieldValue(VISCERAL_FAT_MASS_FIELD_NUM, value)

    /** bone_mass, in kg. */
    public var boneMass: Double?
        get() = getFieldValue(BONE_MASS_FIELD_NUM) as? Double
        set(value) = setFieldValue(BONE_MASS_FIELD_NUM, value)

    /** muscle_mass, in kg. */
    public var muscleMass: Double?
        get() = getFieldValue(MUSCLE_MASS_FIELD_NUM) as? Double
        set(value) = setFieldValue(MUSCLE_MASS_FIELD_NUM, value)

    /** basal_met, in kcal/day. */
    public var basalMet: Double?
        get() = getFieldValue(BASAL_MET_FIELD_NUM) as? Double
        set(value) = setFieldValue(BASAL_MET_FIELD_NUM, value)

    /** physique_rating. */
    public var physiqueRating: UByte?
        get() = getFieldValue(PHYSIQUE_RATING_FIELD_NUM) as? UByte
        set(value) = setFieldValue(PHYSIQUE_RATING_FIELD_NUM, value)

    /** active_met, in kcal/day. ~4kJ per kcal, 0.25 allows max 16384 kcal */
    public var activeMet: Double?
        get() = getFieldValue(ACTIVE_MET_FIELD_NUM) as? Double
        set(value) = setFieldValue(ACTIVE_MET_FIELD_NUM, value)

    /** metabolic_age, in years. */
    public var metabolicAge: UByte?
        get() = getFieldValue(METABOLIC_AGE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(METABOLIC_AGE_FIELD_NUM, value)

    /** visceral_fat_rating. */
    public var visceralFatRating: UByte?
        get() = getFieldValue(VISCERAL_FAT_RATING_FIELD_NUM) as? UByte
        set(value) = setFieldValue(VISCERAL_FAT_RATING_FIELD_NUM, value)

    /** user_profile_index. Associates this weight scale message to a user. This corresponds to the index of the user profile message in the weight scale file. */
    public var userProfileIndex: UShort?
        get() = getFieldValue(USER_PROFILE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(USER_PROFILE_INDEX_FIELD_NUM, value)

    /** bmi, in kg/m^2. */
    public var bmi: Double?
        get() = getFieldValue(BMI_FIELD_NUM) as? Double
        set(value) = setFieldValue(BMI_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val WEIGHT_FIELD_NUM: UByte = 0u
        public const val PERCENT_FAT_FIELD_NUM: UByte = 1u
        public const val PERCENT_HYDRATION_FIELD_NUM: UByte = 2u
        public const val VISCERAL_FAT_MASS_FIELD_NUM: UByte = 3u
        public const val BONE_MASS_FIELD_NUM: UByte = 4u
        public const val MUSCLE_MASS_FIELD_NUM: UByte = 5u
        public const val BASAL_MET_FIELD_NUM: UByte = 7u
        public const val PHYSIQUE_RATING_FIELD_NUM: UByte = 8u
        public const val ACTIVE_MET_FIELD_NUM: UByte = 9u
        public const val METABOLIC_AGE_FIELD_NUM: UByte = 10u
        public const val VISCERAL_FAT_RATING_FIELD_NUM: UByte = 11u
        public const val USER_PROFILE_INDEX_FIELD_NUM: UByte = 12u
        public const val BMI_FIELD_NUM: UByte = 13u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("Weight", 0u, BaseType.UINT16, 100.0, 0.0, "kg", false)
            fields[1] = Field("PercentFat", 1u, BaseType.UINT16, 100.0, 0.0, "%", false)
            fields[2] = Field("PercentHydration", 2u, BaseType.UINT16, 100.0, 0.0, "%", false)
            fields[3] = Field("VisceralFatMass", 3u, BaseType.UINT16, 100.0, 0.0, "kg", false)
            fields[4] = Field("BoneMass", 4u, BaseType.UINT16, 100.0, 0.0, "kg", false)
            fields[5] = Field("MuscleMass", 5u, BaseType.UINT16, 100.0, 0.0, "kg", false)
            fields[7] = Field("BasalMet", 7u, BaseType.UINT16, 4.0, 0.0, "kcal/day", false)
            fields[8] = Field("PhysiqueRating", 8u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[9] = Field("ActiveMet", 9u, BaseType.UINT16, 4.0, 0.0, "kcal/day", false)
            fields[10] = Field("MetabolicAge", 10u, BaseType.UINT8, 1.0, 0.0, "years", false)
            fields[11] = Field("VisceralFatRating", 11u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[12] = Field("UserProfileIndex", 12u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[13] = Field("Bmi", 13u, BaseType.UINT16, 10.0, 0.0, "kg/m^2", false)
            return fields
        }
    }
}
